import { Link, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  Bell,
  Download,
  HelpCircle,
  LifeBuoy,
  LogOut,
  Settings,
  User as UserIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { isNavPathActive, mostSpecificNavPath, ownerPath } from '@/lib/hosts';
import type { NavItem } from '@/lib/navigation';
import { useVisibleNavItems } from '@/hooks/useVisibleNavItems';
import { useAuth } from '@/contexts/AuthContext';
import { useShop } from '@/contexts/ShopContext';
import { useAppDownloadStore } from '@/stores/appDownloadStore';
import {
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { OwnerDropdownContent } from '@/components/owner-ui';
import { SidebarItem } from './SidebarItem';
import { ShopSwitcher } from './ShopSwitcher';
import { rentalWorkspaceLabel } from '@/lib/rental-labels';
import { useCurrentShopDetail } from '@/hooks/queries/useCurrentShopDetail';

const initials = (value: string | null | undefined): string => {
  if (!value) return '?';
  const parts = value.trim().split(/\s+/);
  if (parts.length === 0) return '?';
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase() || '?';
};

const isItemActive = (pathname: string, href: string): boolean =>
  isNavPathActive(pathname, ownerPath(href));

interface SidebarProps {
  /** Render mode: `rail` = sticky vertical 88px (desktop), `drawer` = full-width inline (mobile drawer). */
  variant?: 'rail' | 'drawer';
  /** Optional callback fired after a nav click in drawer mode (to close the sheet). */
  onNavigate?: () => void;
}

/**
 * Vertical rail (88px) with HeyGen-inspired layout.
 *
 * Phase 8 iteration 3 :
 * - Top: real Velko app icon SVG (clic = direct vers /dashboard, ZÉRO popover).
 * - Middle: nav items (Sales/Products/Stock/Reports/Compte) with hover flyouts.
 * - Bottom (in order): ShopSwitcherRail (avatar boutique active + popover liste),
 *   Bell, Help, then UserAvatar with DropdownMenu (Profile/Settings/Logout).
 */
export function Sidebar({ variant = 'rail', onNavigate }: SidebarProps = {}) {
  const { pathname } = useLocation();
  const { user, logout } = useAuth();
  const { t } = useTranslation(['navigation', 'common']);
  const { shops } = useShop();
  const { data: shopDetailResponse } = useCurrentShopDetail();
  const shopDetail = shopDetailResponse?.data;
  const rentalLabel = rentalWorkspaceLabel({
    category: shopDetail?.category as string | null | undefined,
    profile: shopDetail?.rental_profile as string | null | undefined,
  });
  const openDownloadModal = useAppDownloadStore((s) => s.openModal);

  const visibleItems: NavItem[] = useVisibleNavItems();

  const userName = user?.name ?? user?.email ?? '';
  const userEmail = user?.email ?? '';
  const isDrawer = variant === 'drawer';

  const handleLogout = async () => {
    await logout();
  };

  // ============================================================
  // LOGO — direct link to /dashboard (no popover)
  // ============================================================
  const logoButton = (
    <Link
      to={ownerPath('/dashboard')}
      onClick={onNavigate}
      aria-label={t('navigation:go_to_dashboard', { defaultValue: 'Tableau de bord' })}
      className={cn(
        'relative flex h-11 w-11 items-center justify-center rounded-xl',
        'transition-transform hover:scale-[1.04] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime-400 focus-visible:ring-offset-2',
        'shadow-[0_0_0_2px_rgba(163,230,53,0.18),0_8px_16px_-8px_rgba(163,230,53,0.5)]',
      )}
    >
      <img
        src="/brand/velko-appicon.svg"
        alt="Velko"
        width={44}
        height={44}
        className="block h-11 w-11 rounded-xl"
        draggable={false}
      />
      <span
        aria-hidden="true"
        className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-[2.5px] border-white bg-lime-400 dark:border-charcoal-900"
      />
    </Link>
  );

  // ============================================================
  // NAV ITEMS (shared between rail and drawer modes)
  // ============================================================
  const navItems = (
    <nav
      className={cn(
        isDrawer
          ? 'flex flex-1 flex-col gap-1 px-3 py-2'
          : 'flex flex-1 flex-col items-center gap-0.5',
      )}
    >
      {visibleItems.map((item, idx) => {
        const active = isItemActive(pathname, item.href);
        const children = item.children ?? [];
        const childActiveHref = mostSpecificNavPath(
          pathname,
          children.map((child) => ownerPath(child.href)),
        );
        const hasChildren = children.length > 0;

        const divider = idx > 0 && item.labelKey === 'account' && (
          <span
            aria-hidden="true"
            className={cn(
              isDrawer
                ? 'my-2 h-px w-full bg-slate-200 dark:bg-charcoal-700'
                : 'my-2 h-px w-8 bg-slate-200 dark:bg-charcoal-700',
            )}
          />
        );

        if (isDrawer) {
          return (
            <div key={item.href} className="flex flex-col">
              {divider}
              <Link
                to={ownerPath(item.href)}
                onClick={onNavigate}
                className={cn(
                  'flex items-center gap-3 rounded-lg px-3 py-2.5 text-[14px] font-medium transition-colors',
                  'text-slate-600 hover:bg-slate-100 hover:text-charcoal-950 dark:text-slate-300 dark:hover:bg-charcoal-800 dark:hover:text-slate-50',
                  (active || childActiveHref) &&
                    'bg-lime-50 text-charcoal-950 dark:bg-lime-500/12 dark:text-lime-300',
                )}
              >
                <item.icon className="h-4.5 w-4.5" strokeWidth={1.75} aria-hidden="true" />
                <span className="flex-1">
                  {item.labelKey === 'rental' ? rentalLabel : t(`navigation:${item.labelKey}`)}
                </span>
              </Link>
              {hasChildren ? (
                <ul className="ml-9 mt-0.5 flex flex-col gap-0.5 border-l border-slate-200 pl-3 dark:border-charcoal-700">
                  {children.map((child) => {
                    const ChildIcon = child.icon;
                    const isChildActive = ownerPath(child.href) === childActiveHref;
                    return (
                      <li key={child.href}>
                        <Link
                          to={ownerPath(child.href)}
                          onClick={onNavigate}
                          className={cn(
                            'flex items-center gap-2.5 rounded-md px-2 py-1.5 text-[13px] font-medium transition-colors',
                            'text-slate-500 hover:bg-slate-100 hover:text-charcoal-950 dark:text-slate-400 dark:hover:bg-charcoal-800 dark:hover:text-slate-50',
                            isChildActive && 'text-charcoal-950 dark:text-lime-300',
                          )}
                        >
                          {ChildIcon ? (
                            <ChildIcon className="h-3.5 w-3.5" strokeWidth={2} aria-hidden="true" />
                          ) : null}
                          <span>{t(`navigation:${child.labelKey}`)}</span>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              ) : null}
            </div>
          );
        }

        // Rail mode (desktop) — no more hover flyout. Click toggles the
        // persistent <RailSubnavPanel> via SubNavContext.
        return (
          <div key={item.href} className="flex flex-col items-center">
            {divider}
            <SidebarItem
              icon={item.icon}
              labelKey={item.labelKey}
              label={item.labelKey === 'rental' ? rentalLabel : undefined}
              href={item.href}
              isActive={active || Boolean(childActiveHref)}
              hasFlyout={hasChildren}
              navItem={item}
            />
          </div>
        );
      })}
    </nav>
  );

  // ============================================================
  // BOTTOM UTILITIES — shop switcher + bell + help + user dropdown
  // ============================================================
  const userAvatarDropdown = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={userName}
          className={cn(
            isDrawer
              ? 'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-[13px] hover:bg-slate-100 dark:hover:bg-charcoal-800'
              : 'relative mt-1 flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 font-head text-[12px] font-semibold text-charcoal-950 transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime-400 focus-visible:ring-offset-2 dark:bg-charcoal-700 dark:text-slate-50',
          )}
        >
          {isDrawer ? (
            <>
              <span className="grid h-8 w-8 place-items-center rounded-full bg-slate-100 font-head text-[11px] font-bold text-charcoal-950 dark:bg-charcoal-800 dark:text-slate-100">
                {initials(userName)}
              </span>
              <span className="flex min-w-0 flex-col leading-tight">
                <span className="truncate font-semibold text-charcoal-950 dark:text-slate-50">
                  {userName}
                </span>
                <span className="truncate text-[11px] text-slate-500 dark:text-slate-400">
                  {userEmail}
                </span>
              </span>
            </>
          ) : (
            <>
              {initials(userName)}
              <span
                aria-hidden="true"
                className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-white bg-lime-400 dark:border-charcoal-900"
              />
            </>
          )}
        </button>
      </DropdownMenuTrigger>
      <OwnerDropdownContent
        side={isDrawer ? 'top' : 'right'}
        align={isDrawer ? 'start' : 'end'}
        sideOffset={isDrawer ? 8 : 14}
        className="w-56"
      >
        <DropdownMenuLabel>
          <div className="flex flex-col">
            <span className="text-sm font-medium">{userName}</span>
            <span className="text-xs text-muted-foreground">{userEmail}</span>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to={ownerPath('/settings/profile')} onClick={onNavigate} className="gap-2">
            <UserIcon className="size-3.5" />
            {t('navigation:profile', { defaultValue: 'Profil' })}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to={ownerPath('/settings/shop')} onClick={onNavigate} className="gap-2">
            <Settings className="size-3.5" />
            {t('navigation:shop_settings', { defaultValue: 'Réglages boutique' })}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to={ownerPath('/support')} onClick={onNavigate} className="gap-2">
            <LifeBuoy className="size-3.5" />
            {t('navigation:support', { defaultValue: 'Support' })}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() => {
            onNavigate?.();
            openDownloadModal();
          }}
          className="gap-2"
        >
          <Download className="size-3.5" />
          {t('navigation:download_app', { defaultValue: 'Télécharger l’application' })}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem variant="destructive" onSelect={handleLogout}>
          <LogOut className="size-3.5" />
          {t('common:logout', { defaultValue: 'Se déconnecter' })}
        </DropdownMenuItem>
      </OwnerDropdownContent>
    </DropdownMenu>
  );

  const bottomUtils = (
    <div
      className={cn(
        isDrawer
          ? 'mt-auto flex flex-col gap-0.5 border-t border-slate-200 px-3 py-3 dark:border-charcoal-700'
          : 'flex flex-col items-center gap-0.5',
      )}
    >
      {/* Shop switcher (above bell/help/user) */}
      {shops.length > 0 ? (
        <ShopSwitcher variant={isDrawer ? 'drawer' : 'rail'} onNavigate={onNavigate} />
      ) : null}

      <Link
        to={ownerPath('/settings/notifications')}
        onClick={onNavigate}
        aria-label={t('navigation:notifications', { defaultValue: 'Notifications' })}
        className={cn(
          isDrawer
            ? 'flex items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-charcoal-800'
            : 'relative flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-slate-100 hover:text-charcoal-950 dark:text-slate-400 dark:hover:bg-charcoal-800',
        )}
      >
        <Bell className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" />
        {isDrawer ? (
          <span>{t('navigation:notifications', { defaultValue: 'Notifications' })}</span>
        ) : null}
      </Link>
      <Link
        to={ownerPath('/support')}
        onClick={onNavigate}
        aria-label={t('navigation:support', { defaultValue: 'Aide' })}
        data-tour={isDrawer ? undefined : 'rail-help'}
        className={cn(
          isDrawer
            ? 'flex items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-charcoal-800'
            : 'flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-slate-100 hover:text-charcoal-950 dark:text-slate-400 dark:hover:bg-charcoal-800',
        )}
      >
        <HelpCircle className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" />
        {isDrawer ? <span>{t('navigation:support', { defaultValue: 'Aide' })}</span> : null}
      </Link>

      {userAvatarDropdown}
    </div>
  );

  // ============================================================
  // ROOT — rail or drawer
  // ============================================================
  if (isDrawer) {
    return (
      <div className="flex h-full flex-col bg-white dark:bg-charcoal-900">
        <div className="flex items-center gap-3 border-b border-slate-200 px-3 py-3 dark:border-charcoal-700">
          {logoButton}
          <div className="min-w-0 flex-1">
            <p className="truncate font-head text-[14px] font-semibold text-charcoal-950 dark:text-slate-50">
              Velko
            </p>
            <p className="font-mono text-[10px] text-slate-400 dark:text-slate-500">
              {t('navigation:tap_logo_dashboard', { defaultValue: 'Aller au tableau de bord' })}
            </p>
          </div>
        </div>
        {navItems}
        {bottomUtils}
      </div>
    );
  }

  return (
    <>
      {/* Permanent spacer to keep main content offset from the fixed sidebar */}
      <div className="hidden w-[88px] shrink-0 lg:block" aria-hidden="true" />

      <aside
        aria-label={t('navigation:dashboard')}
        data-tour="sidebar"
        className={cn(
          'fixed left-0 top-0 z-40 hidden h-screen w-[88px] flex-col items-center bg-white',
          'border-r border-slate-200 px-1.5 py-3',
          'dark:border-charcoal-700 dark:bg-charcoal-900',
          'lg:flex',
        )}
      >
        <div className="mb-3.5">{logoButton}</div>

        {navItems}

        {bottomUtils}
      </aside>
    </>
  );
}
